/**
 * Zoom's recipes as data (Mac recipes R3), from the research (comms/zoom.md). Each passes the program
 * lint and ships held until the Zoom drill lifts it. Zoom has no AppleScript, its documented chords are
 * toggles on the key window, and its in-meeting menu is built in code under a name the research could
 * not read offline, so most meeting acts wait on the R3 review lists for that harvest. Joining, starting,
 * recording and leaving are outward and wait for a yes.
 */
import { opening, recipeApp, type OsRecipe } from '../os-recipes.js';

const ZOOM = recipeApp('us.zoom.xos');

/** A reversible chord in the running app. */
const chord = (id: string, title: string, description: string, key: string, cues: readonly string[], reply: string): OsRecipe => ({
  id, app: ZOOM, status: 'held', title, description, params: {}, steps: [{ op: 'activate' }, { op: 'key', chord: key }], tier: 'reversible', cues, reply,
});

export const RECIPES: readonly OsRecipe[] = [
  // ── Meetings ──────────────────────────────────────────────────────────────────────────────────
  // No yes word fits "start": only "yes" answers it.
  {
    id: 'zoom.start_meeting', app: ZOOM, status: 'held',
    title: 'Start an instant Zoom meeting',
    description: 'A new instant meeting of their own, and only on their yes. For asks like "start a zoom meeting" or "start a new zoom call".',
    params: {},
    steps: [{ op: 'activate' }, { op: 'memo' }],
    onYes: [{ op: 'send_key', chord: 'cmd+ctrl+v' }],
    tier: 'irreversible', cues: ['start', 'new', 'instant', 'spin', 'shuru', 'meeting', 'new zoom call'],
    reply: 'Start a new Zoom meeting now?',
  },
  opening(chord('zoom.settings', 'Open Zoom settings', 'For asks like "open zoom settings" or "zoom preferences".', 'cmd+,', ['settings', 'preferences', 'prefs', 'setting', 'audio settings', 'change my zoom settings'], 'Opened Zoom settings.')),
  // ── In a meeting ──────────────────────────────────────────────────────────────────────────────
  opening(chord('zoom.participants', 'Show Zoom participants', 'A toggle on the key window: an open list closes. For asks like "who\'s in this zoom" or "show participants".', 'cmd+u', ['participants', 'people', "who's", 'attendees', 'kaun', 'participants list', 'kaun hai meeting', 'people panel'], 'Toggled the participants list in Zoom.')),
  // drill: the front window is the meeting ("Zoom Meeting"), not Zoom Workplace home, before this is lifted
  {
    id: 'zoom.full_screen_on', app: ZOOM, status: 'held',
    title: 'Put the Zoom meeting in full screen',
    description: 'Acts on the front Zoom window, which may be the home window rather than the meeting. For asks like "full screen zoom" or "make the meeting full screen".',
    params: {},
    steps: [{ op: 'activate' }, { op: 'verb', id: 'sysevents.window_fullscreen_set', params: { enabled: true } }],
    tier: 'reversible', cues: ['full', 'fullscreen', 'maximize', 'full screen', 'meeting full screen'],
    reply: 'Put the Zoom meeting in full screen.',
  },
];
